import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "../../services/apiService";
import { ContactRequest, User } from '../../types';
import { UserState } from "../../types";
import { apiEndpoints } from "../../config/apiEndpoints";
import { isAxiosError } from "axios";
import { RootState } from "../../store/store";


const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  clientId: null,
  surveyType: null,
  isDashboardLaunch: false
};



/**
 * Asynchronous thunk action to authenticate a user (login or logout).
*
* This function handles user authentication, determining whether to perform a login or logout
* based on the `LoginAttempt` flag within the `user` parameter. 
*
* @param {Partial<User>} user - The user object containing authentication details, including LoginAttempt, FirstName, and LastName.
* @returns {Promise<{ sessionData: Partial<User> | null, LoginAttempt: boolean }, Partial<User>, {state:RootState, dispatch: AppDispatch, rejectValue: string}>} A promise resolving to an object containing session data and the LoginAttempt flag.
*/
export const auth = createAsyncThunk(
  "user/auth",
  async (user: Partial<User>, { rejectWithValue, getState }) => {
    try {
      if (user.LoginAttempt) {
        const state = getState() as RootState;
        const currentLanguage = state.lang.language;
        const authPayload = {
          ...user,
          Language: currentLanguage
        };
        const response = await apiClient.post(apiEndpoints.userAuth, authPayload);

        const sessionData = {
          ...response.data,
          FirstName: response.data.FirstName || user.FirstName,
          MiddleName: user.MiddleName,
          LastName: user.LastName,
          IsTestUser: response.data.IsTestUser,
          Language: currentLanguage,
          SurveyStatus: response.data.SurveyStatus
        }
        return { sessionData, LoginAttempt: true };
      } else {
        const logoutPayload = {
          LoginAttempt: false,
          ClientId: user.ClientId,
          SessionId: user.SessionId
        };
        await apiClient.post(apiEndpoints.userAuth, logoutPayload);
        return { sessionData: null, LoginAttempt: false };
      }
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        console.log("US Line 39", error.response.data)
        return rejectWithValue(error.response.data.Message);
      }
      return rejectWithValue(error);
    }
  }
);
export const contactInfo = createAsyncThunk(
  "user/contact",
  async (contact: ContactRequest) => {


    const response = await apiClient.post(apiEndpoints.userContact, contact);

    return response.data;

  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLaunchParams: (state, action: PayloadAction<{ cId: string | null, sType: string | null }>) => {
      state.clientId = action.payload.cId;
      state.surveyType = action.payload.sType;
      // The flag is true if a client ID is present, indicating a dashboard launch
      state.isDashboardLaunch = !!action.payload.cId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(auth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(auth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.LoginAttempt) {
          state.user = action.payload.sessionData; //login
          state.error = null;
        } else {
          state.user = null;//logout
          state.isDashboardLaunch = false;
          state.clientId = null;
          state.surveyType = null;
          state.error = null;
        }
      })
      .addCase(auth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch user";
      })
      .addCase(contactInfo.pending, (state) => {
        state.error = null;
      })
      .addCase(contactInfo.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(contactInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update contact info";
      })
  }
});
export const { clearError, setLaunchParams } = userSlice.actions;
export default userSlice;
